import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Avatar from "../../components/notes/Avatar";
import MediaGallery from "../../components/media/MediaGallery";
import DOMPurify from "dompurify";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import {
  FaClock,
  FaArrowLeft,
  FaComments,
  FaCopy,
  FaCheck,
  FaMapMarkerAlt,
} from "react-icons/fa";
import styles from "./PromotionDetailCard.module.css";

const PromotionDetailCard = ({
  images,
  videos,
  title,
  attachments,
  description,
  cost,
  type,
  state,
  city,
  pin_code,
  photo,
  creater,
  createdOn,
  backBtn,
  discussionPath,
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cleanDescription = (description || "").trim();
  const descriptionWords = cleanDescription ? cleanDescription.split(" ") : [];
  const isLongDescription = descriptionWords.length > 60;
  const displayedDescription =
    isLongDescription && !showFullDescription
      ? descriptionWords.slice(0, 60).join(" ") + "..."
      : cleanDescription;

  const formattedPrice =
    cost !== undefined && cost !== null && cost !== ""
      ? isNaN(Number(cost))
        ? cost
        : `₹ ${Number(cost).toLocaleString("en-IN")}`
      : null;

  const locationString = [city, state].filter(Boolean).join(", ");

  return (
    <div className={styles.pageWrapper}>
      {/* 1. Main Promotion Overview Card */}
      <div className={styles.sectionCard}>
        {/* Top Back Navigation Bar */}
        {(backBtn || discussionPath) && (
          <div className={styles.topNavHeader}>
            <div className={styles.topLeftNav}>
              {backBtn && (
                <Link to={backBtn} className={styles.backBtn}>
                  <FaArrowLeft /> Back to Promotions
                </Link>
              )}
              <span className={styles.pageCategory}>Shared Promotion Details</span>
            </div>

            <div className={styles.headerActions}>
              {discussionPath && (
                <button
                  type="button"
                  className={styles.discussionActionBtn}
                  onClick={() => navigate(discussionPath)}
                  title="View Comments & Discussion"
                >
                  <FaComments />
                  <span>View Discussion</span>
                </button>
              )}

              <button
                type="button"
                className={styles.shareBtn}
                onClick={handleCopyLink}
                title="Copy promotion link"
              >
                {copied ? <FaCheck className="text-success" /> : <FaCopy />}
                <span>{copied ? "Copied" : "Share"}</span>
              </button>
            </div>
          </div>
        )}

        {/* Media Gallery */}
        <div className={styles.mediaWrapper}>
          <MediaGallery
            images={images}
            promotion_videos={videos}
            attachments={attachments}
          />
        </div>

        {/* Overview Grid */}
        <div className={styles.overviewGrid}>
          {/* Left Column: Title, Location, Tags */}
          <div className={styles.leftDetails}>
            <h1 className={styles.promotionTitle}>
              {title || "Promotion Details"}
            </h1>

            {locationString && (
              <div className={styles.locationRow}>
                <FaMapMarkerAlt className={styles.locationIcon} />
                <span>
                  {locationString} {pin_code ? `- ${pin_code}` : ""}
                </span>
              </div>
            )}

            <div className={styles.tagPills}>
              {type && (
                <span
                  className={`${styles.pillBadge} ${styles.pillBadgePrimary}`}
                >
                  {type}
                </span>
              )}
              {city && <span className={styles.pillBadge}>{city}</span>}
              {state && <span className={styles.pillBadge}>{state}</span>}
            </div>
          </div>

          {/* Right Column: Pricing Card & Creator Card */}
          <div className={styles.rightSidebar}>
            {formattedPrice && (
              <div className={styles.priceCard}>
                <span className={styles.priceLabel}>Starting Price</span>
                <span className={styles.priceValue}>{formattedPrice}</span>
              </div>
            )}

            <div className={styles.listedByCard}>
              <div className={styles.creatorLeft}>
                <Avatar src={photo} icon={creater} size={42} />
                <div className={styles.creatorText}>
                  <span className={styles.creatorLabel}>Listed By</span>
                  <span className={styles.creatorName}>
                    {creater || "Partner"}
                  </span>
                </div>
              </div>

              {createdOn && (
                <div className={styles.creatorTime}>
                  <FaClock className="text-muted" />
                  <span>
                    Listed <TimeAgo timestamp={createdOn} />
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description Section */}
        {cleanDescription && (
          <div className={styles.descriptionBlock}>
            <h3 className={styles.blockTitle}>About this Promotion</h3>
            <div className={styles.descriptionContent}>
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(displayedDescription),
                }}
              />
              {isLongDescription && (
                <button
                  type="button"
                  className={styles.readMoreLink}
                  onClick={() =>
                    setShowFullDescription(!showFullDescription)
                  }
                >
                  {showFullDescription ? "Read Less ▲" : "Read More ▼"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionDetailCard;